import React, { useRef, useState } from 'react';

import { useReducedMotion } from 'utils/useReducedMotion';

import * as styled from './styles';

type Props = {
  value: number;
  onChange: (rating: number) => void;
  label?: string;
  disabled?: boolean;
};

const STARS = [1, 2, 3, 4, 5];

export const StarPicker = ({ value, onChange, label, disabled }: Props) => {
  const [hovered, setHovered] = useState(0);
  const reducedMotion = useReducedMotion();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const active = hovered || value;

  const handleKey = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    current: number,
  ) => {
    if (disabled) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.max(1, current - 1);
      onChange(next);
      refs.current[next - 1]?.focus();
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.min(5, current + 1);
      onChange(next);
      refs.current[next - 1]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      onChange(1);
      refs.current[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      onChange(5);
      refs.current[4]?.focus();
    }
  };

  return (
    <styled.Row role="radiogroup" aria-label={label ?? 'Rating'}>
      {STARS.map((starValue, index) => {
        const checked = value === starValue;
        return (
          <styled.Star
            key={starValue}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={`${starValue} of 5`}
            tabIndex={checked || (value === 0 && starValue === 1) ? 0 : -1}
            $active={starValue <= active}
            $reducedMotion={reducedMotion}
            disabled={disabled}
            onClick={() => !disabled && onChange(starValue)}
            onMouseEnter={() => !disabled && setHovered(starValue)}
            onMouseLeave={() => setHovered(0)}
            onKeyDown={(event) => handleKey(event, value || 1)}
          >
            {starValue <= active ? '★' : '☆'}
          </styled.Star>
        );
      })}
    </styled.Row>
  );
};
